import { Formik } from 'formik';
import React, { useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, InlineNotification, Select, SelectItem, Stack, TextArea, TextInput } from '@carbon/react';
import { StorefrontContactMessageEntity } from 'src/services/apis/types';

/**
 * `StorefrontMessageStatusEnum.NEW` / `.HANDLED` / `.SPAM` — see the full note on
 * `STOREFRONT_MESSAGE_STATUS` in `src/features/storefrontMessages.tsx`. Repeated rather than
 * imported from there: that file imports THIS component, so importing the constant back would
 * be a cycle whose module evaluation order leaves the object undefined at this line.
 */
const NEW = 1;
const HANDLED = 2;
const SPAM = 3;

interface MessageDetailFormProps {
  onSubmit: (values: any) => void;
  errorMessage: string | null;
  initialValues: StorefrontContactMessageEntity | null | undefined;
}

/**
 * The crud layer has no read-only detail modal, so a message is displayed in its edit form:
 * everything the visitor wrote is disabled, and the only live control is `status`. Nothing
 * here may imply anything about delivery — the inbox is the record, and the notification
 * email is best-effort (spec §4 decision 22).
 */
const MessageDetailForm = (props: MessageDetailFormProps, ref) => {
  const { t } = useTranslation();
  const formikRef = useRef<any>();

  useImperativeHandle(ref, () => ({
    submitForm: formikRef.current?.submitForm,
    setFieldValue: formikRef.current?.setFieldValue,
    // No hardcoded status: Formik's own `initialValues` are the row being shown (kept in step
    // with the `initialValues` prop by `enableReinitialize`), so a reset restores that row's
    // status rather than inventing `new`.
    resetForm: () => formikRef.current?.resetForm(),
  }));

  return (
    <div>
      {props.errorMessage && <InlineNotification subtitle={props.errorMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={{
          id: props.initialValues?.id,
          name: props.initialValues?.name ?? '',
          email: props.initialValues?.email ?? '',
          phone: props.initialValues?.phone ?? '',
          message: props.initialValues?.message ?? '',
          createdAt: props.initialValues?.createdAt ?? '',
          // The API returns a number (1/2/3) while a Carbon Select only deals in strings.
          status: String(props.initialValues?.status ?? NEW),
          handledAt: props.initialValues?.handledAt ?? '',
        }}
        onSubmit={props.onSubmit}
      >
        {({ values, setFieldValue }) => (
          <FormGroup legendText={''}>
            <Stack gap={5}>
              {/* The visitor's own words, shown but not editable. */}
              <TextInput id="message-name" labelText={t('storefrontMessages.name')} value={values.name} disabled />
              <TextInput id="message-email" labelText={t('storefrontMessages.email')} value={values.email} disabled />
              <TextInput id="message-phone" labelText={t('storefrontMessages.phone')} value={values.phone} disabled />
              <TextInput
                id="message-received-at"
                labelText={t('storefrontMessages.receivedAt')}
                value={values.createdAt}
                disabled
              />
              <TextArea
                id="message-body"
                labelText={t('storefrontMessages.message')}
                value={values.message}
                rows={6}
                disabled
              />
              {/* The one writable field: triage. `handledAt` rides along invisibly, stamped
               * when the message leaves the inbox and cleared when it returns to `new`. */}
              <Select
                id="message-status"
                labelText={t('storefrontMessages.status')}
                value={values.status}
                onChange={(event) => {
                  const next = event.target.value;
                  setFieldValue('status', next);
                  setFieldValue(
                    'handledAt',
                    next === String(HANDLED) || next === String(SPAM) ? new Date().toISOString() : ''
                  );
                }}
              >
                <SelectItem value={String(NEW)} text={t('storefrontMessages.statusNew')} />
                <SelectItem value={String(HANDLED)} text={t('storefrontMessages.statusHandled')} />
                <SelectItem value={String(SPAM)} text={t('storefrontMessages.statusSpam')} />
              </Select>
            </Stack>
          </FormGroup>
        )}
      </Formik>
    </div>
  );
};

export default React.forwardRef(MessageDetailForm);
